import { Badge } from "@design";
import { ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatDate, formatList } from "@/i18n/format";
import { ActionPreview } from "./ActionPreview";
import type { ActionDescription, ActionDestination, ActionField, ActionPerson, ActionWhen } from "./actionModel";

/**
 * What a write action does (MHS-971): its title and the exact values it will send. Shared by the chat's approval card
 * and the approvals inbox, so an action reads the same wherever it's decided.
 */
export function ActionSummary({
  action,
  omit = [],
  status,
}: Readonly<{
  action: ActionDescription;
  /** Fields shown elsewhere (being edited). */
  omit?: string[];
  /** Where it stands (Approved, Done…), in place of "Approval needed". */
  status?: ReactNode;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  // A meeting with no one else invited is time on the person's own calendar.
  const named =
    action.named === "create_calendar_event" && action.kind === "meeting" && action.people.length === 0
      ? "create_calendar_event_solo"
      : action.named;
  const title = named ? t(`title.${named}`) : t("title.generic", { tool: action.displayName });
  const fields = action.fields.filter((field) => !omit.includes(field.name));
  return (
    <>
      <div className="flex flex-col items-start gap-2">
        {status ?? (
          <Badge variant="neutral" icon={<ShieldCheck size={12} />}>
            {t("label")}
          </Badge>
        )}
        <Text variant="h3" className="min-w-0 break-words">
          {title}
        </Text>
      </div>
      {action.destination && <Destination destination={action.destination} inThread={action.inThread} />}
      {action.when && <When when={action.when} online={action.online} />}
      <ActionPreview action={action} />
      {action.people.length > 0 && <People people={action.people} />}
      {fields.length > 0 && <ActionFields fields={fields} />}
    </>
  );
}

/** Where it goes, as the server found it: "To: Michael Koert" over "@michael.koert, Direct message". */
function Destination({ destination, inThread }: Readonly<{ destination: ActionDestination; inThread: boolean }>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  const kind = t(`destination.${destination.kind}`);
  const notes = [destination.detail, destination.label ? kind : null, inThread ? t("inThread") : null].filter(
    (note): note is string => Boolean(note),
  );
  return (
    <div className="space-y-0.5">
      <Text variant="caption" tone="tertiary">
        {t("to")}
      </Text>
      <Text variant="body-sm" className="break-words">
        {destination.label ?? kind}
      </Text>
      {notes.length > 0 && (
        <Text variant="caption" tone="secondary" className="break-words">
          {formatList(notes)}
        </Text>
      )}
    </div>
  );
}

/** The values the call will send, as the model wrote them. */
function ActionFields({ fields }: Readonly<{ fields: ActionField[] }>) {
  return (
    <dl className="space-y-2">
      {fields.map((field) => (
        <div key={field.name} className="space-y-0.5">
          <dt>
            <Text variant="caption" tone="tertiary">
              {field.name}
            </Text>
          </dt>
          <dd>
            {field.block ? (
              <Text
                variant="body-sm"
                className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-surface-inset p-2"
              >
                {field.value}
              </Text>
            ) : (
              <Text variant="body-sm" className="break-words">
                {field.value}
              </Text>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
const DAY_TIME: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric", ...TIME };

/** "Thursday, October 30, 2:00 – 2:30 PM", in the viewer's time zone, and whether it has a Teams link. */
function When({ when, online }: Readonly<{ when: ActionWhen; online: boolean }>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  const range = when.end
    ? t("timeRange", { start: formatDate(when.start, DAY_TIME), end: formatDate(when.end, TIME) })
    : formatDate(when.start, DAY_TIME);
  return (
    <div className="space-y-0.5">
      <Text variant="caption" tone="tertiary">
        {t("when")}
      </Text>
      <Text variant="body-sm">{range}</Text>
      {online && (
        <Text variant="caption" tone="secondary">
          {t("teamsLink")}
        </Text>
      )}
    </div>
  );
}

/** Who it reaches, with anyone outside the company marked. */
function People({ people }: Readonly<{ people: ActionPerson[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  const roles = [...new Set(people.map((p) => p.role))];
  return (
    <div className="space-y-2">
      {roles.map((role) => (
        <div key={role} className="space-y-1">
          <Text variant="caption" tone="tertiary">
            {t(`people.${role}`)}
          </Text>
          <ul className="flex flex-wrap gap-1.5">
            {people
              .filter((p) => p.role === role)
              .map((p) => (
                <li key={p.address}>
                  <Badge variant={p.external ? "warning" : "neutral"}>
                    {p.external ? t("people.external", { address: p.address }) : p.address}
                  </Badge>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
